import React, { FC, useMemo } from "react";
import { GroupType, UserType } from "@src/types.generated";
import MultiSelectMenuContent from "@src/lib/ui/MultiSelectMenuContent";
import useLocations from "@src/features/locations/hooks/useLocations";
import useRoles from "@src/features/roles/hooks/useRoles";
import SimpleDropdown from "../../libraryItem/course/SimpleDropdown";
import { cn } from "@src/lib/utils/cn";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";

export type Choice = {
  value: string;
  label: string;
};

type Props = {
  type: GroupType;
  selectedGroupIds: number[];
  setSelectedGroupIds: (groupIds: number[]) => void;
  userType: UserType;
  error?: boolean;
};

const GroupMultiSelectInput: FC<Props> = ({
  type,
  setSelectedGroupIds,
  selectedGroupIds,
  userType,
  error = false,
}) => {
  const { locations, loading: loadingLocations } = useLocations();
  const { roles, loading: loadingRoles } = useRoles();
  const allChoices: Choice[] = useMemo(() => {
    if (type === GroupType.Location) {
      return locations.map((l) => ({
        label: l.name,
        value: l.id.toString(),
      }));
    } else {
      return roles.map((r) => ({
        label: r.name,
        value: r.id.toString(),
      }));
    }
  }, [locations, roles, type]);
  const selectedChoices: Choice[] = useMemo(() => {
    return allChoices.filter((c) => selectedGroupIds.includes(Number(c.value)));
  }, [selectedGroupIds, allChoices]);
  const onSelectedChoicesChange = (newChoices: Choice[]): void => {
    setSelectedGroupIds(newChoices.map((nc) => Number(nc.value)));
  };
  const inputText = useMemo(
    () => selectedChoices.map((c) => c.label).join(", "),
    [selectedChoices],
  );
  const placeholderText = useMemo(() => {
    return type === GroupType.Location
      ? "Select locations..."
      : "Select roles...";
  }, [type]);

  return (
    <SimpleDropdown
      trigger={
        userType !== UserType.Admin || type !== GroupType.Location ? (
          <div
            className={cn(
              "border border-gray-5-alpha box-border rounded-lg text-on-background leading-[18px] text-sm p-3 h-10 shadow-[0_2px_3px_rgba(0,0,0,0.03)] focus:outline focus:outline-blue-3 focus:border-blue-5 hover:border-[#b9bac6] [&.error]:border-red-9",
              "w-full flex items-center justify-between flex-row pt-1 pb-1 h-auto cursor-pointer",
              error &&
                "border border-red-9 hover:border-red-9 focus:border-red-9",
            )}
          >
            {selectedChoices.length > 0 ? (
              inputText
            ) : (
              <span className={cn("text-gray-7")}>{placeholderText}</span>
            )}
            <ChevronDownIcon className="ml-1 size-4 text-gray-5" />
          </div>
        ) : (
          <div
            className={cn(
              "border border-gray-5-alpha bg-gray-3-alpha box-border rounded-lg text-on-background leading-6 text-sm p-3 h-10 pt-2 pb-2",
            )}
          >
            <span className={cn("text-gray-7")}>
              Admins manage all locations by default
            </span>
          </div>
        )
      }
      content={
        <MultiSelectMenuContent
          allChoices={allChoices}
          selectedChoices={selectedChoices}
          onSelectedChoicesChange={onSelectedChoicesChange}
          loading={loadingLocations || loadingRoles}
          maxMenuHeight={300}
          removeClearButton={true}
          autoFocus
        />
      }
    />
  );
};

export default GroupMultiSelectInput;
